// The /tg page's one fetch helper. It talks only to the tg-team function
// (step A) with Telegram's signed initData. No Hub session, no Hub queries.

export type TgFailure =
  | 'expired'
  | 'closed'
  | 'stale'
  | 'unknown_option'
  | 'not_found'
  | 'rate_limited'
  | 'server'
  | 'offline';

export class TgError extends Error {
  code: TgFailure;
  constructor(code: TgFailure) {
    super(code);
    this.code = code;
  }
}

export interface TgItem {
  id: string;
  created_at: string;
  agent: string;
  priority: string;
  line: string;
  state: string;
}

export interface TgOption {
  key: string;
  label: string;
}

export const decisionScopeFields = [
  'verb',
  'scope',
  'evidence',
  'limits',
  'recovery',
  'target',
  'revision',
] as const;
type DecisionScope = Record<(typeof decisionScopeFields)[number], string> & {
  option_actions: Record<string, 'approve' | 'reject' | 'hold' | 'ask'>;
};

export interface TgDecision {
  id: string;
  created_at: string;
  question: string;
  recommendation: string | null;
  default_if_skipped: string | null;
  options: TgOption[];
  answer: string | null;
  answered_at: string | null;
  answered_via: string | null;
  /** Present only for Tide-managed decisions, from the same database read as the text. */
  decision_version?: number;
  decision_details?: DecisionScope;
  decision_state?: 'pending' | 'held' | 'approved' | 'rejected' | 'stale';
}

export function hasDecisionScope(d: TgDecision): boolean {
  return (
    decisionScopeFields.every(
      (key) =>
        typeof d.decision_details?.[key] === 'string' && d.decision_details[key].trim().length > 0,
    ) &&
    !!d.decision_details?.option_actions &&
    d.options.every((o) =>
      ['approve', 'reject', 'hold', 'ask'].includes(
        d.decision_details!.option_actions[o.key] ?? '',
      ),
    )
  );
}

/** HTTP status to the word the page speaks (tg-team helpers.ts answerResult and authorizeRequest). */
export function failureFor(status: number): TgFailure {
  if (status === 401 || status === 403) return 'expired';
  if (status === 409) return 'closed';
  if (status === 422) return 'unknown_option';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  return 'server';
}

export async function callTg(
  initData: string,
  action: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const root = String(import.meta.env.VITE_SUPABASE_URL ?? '').replace(/\/$/, '');
  let response: Response;
  try {
    response = await fetch(`${root}/functions/v1/tg-team`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...action, initData }),
    });
  } catch {
    throw new TgError('offline');
  }
  if (!response.ok) {
    if (response.status === 409) {
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        /* Keep the legacy conflict fallback. */
      }
      if (typeof body === 'object' && body !== null && 'error' in body && body.error === 'stale') {
        throw new TgError('stale');
      }
    }
    throw new TgError(failureFor(response.status));
  }
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    throw new TgError('server');
  }
}

/** Telegram's signed string: the script's value first, the launch link's hash as the fallback. */
export function readInitData(): string {
  const fromScript = window.Telegram?.WebApp?.initData;
  if (fromScript) return fromScript;
  return new URLSearchParams(window.location.hash.replace(/^#/, '')).get('tgWebAppData') ?? '';
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string;
        ready?: () => void;
        expand?: () => void;
        HapticFeedback?: { notificationOccurred?: (kind: string) => void };
      };
    };
  }
}

/** Loads telegram-web-app.js once, only when the page does not already have it. Never rejects. */
export function loadTelegramScript(): Promise<void> {
  if (window.Telegram?.WebApp) return Promise.resolve();
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js';
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
    window.setTimeout(resolve, 3000);
  });
}

// Step D: Team tab and agent chats (tg-team get_team, get_thread, post_to_agent).
export interface TgTeamAgent {
  agent: string;
  room_ready: boolean;
  unread: number;
  last: { text: string; at: string; mine: boolean } | null;
}

export interface TgDevStatus {
  agent: string;
  state: string;
  line: string;
  at: string;
}

export interface TgChatMessage {
  id: string;
  at: string;
  mine: boolean;
  text: string;
}
